import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, X, Copy, Check, AlertTriangle, Eye, Tag } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { toast } from '../hooks/use-toast';

// El texto del anuncio, leido de la foto que ya esta hecha.
//
// Se abre desde una creacion terminada porque ese es el momento en que al
// vendedor le falta exactamente esto: la fotografia esta lista y lo que le queda
// por delante es el formulario. Cada campo se copia por separado en vez de
// entregar un bloque, porque el destino son ocho cajas distintas de otra web y
// nadie quiere recortar un parrafo a mano para pegar un titulo.

function Field({ label, hint, value, testid, multiline }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1400);
    } catch {
      toast({ title: 'The browser would not let us copy', description: 'Select the text and copy it by hand.' });
    }
  };
  return (
    <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[11px] uppercase tracking-wide text-white/40">{label}</span>
        <span className="flex items-center gap-2">
          {hint && <span className="text-[10px] tabular-nums text-white/25">{hint}</span>}
          <button onClick={copy} data-testid={`listing-copy-${testid}`}
                  title="Copy" className="text-white/35 hover:text-white">
            {done ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
          </button>
        </span>
      </div>
      <p className={`mt-1.5 text-sm text-white/85 ${multiline ? 'whitespace-pre-wrap leading-relaxed' : ''}`}>
        {value}
      </p>
    </div>
  );
}

export default function ListingCopy({ generation, onClose, onSpent }) {
  const [meta, setMeta] = useState(null);
  const [marketplace, setMarketplace] = useState('etsy');
  const [notes, setNotes] = useState('');
  const [brand, setBrand] = useState('');
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  useEffect(() => {
    api.listingMarketplaces()
      .then((m) => { setMeta(m); setMarketplace(m.default); })
      .catch((e) => toast({ title: 'Could not load the marketplaces', description: e.message }));
  }, []);

  const write = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.listingCopy({
        generationId: generation.id, marketplace, notes, brand,
      });
      setOut(r);
      if (r.creditsCharged) {
        onSpent?.();
        // Esta pagina no lleva contador de creditos, asi que el cobro se dice
        // aqui o no se dice en ningun sitio.
        toast({ title: `${r.creditsCharged} credits spent`,
                description: 'Check "What it saw" before you paste any of this.' });
      }
    } catch (e) {
      // El 402 llega con el saldo dentro, y decirlo ahorra un viaje a la
      // pantalla de creditos para averiguar cuanto falta.
      const d = e.detail;
      toast({
        title: e.status === 402 ? 'Not enough credits' : 'Could not write the listing',
        description: e.status === 402 && d?.needed
          ? `This costs ${d.needed} credits and you have ${d.balance}.`
          : e.message,
      });
    } finally { setBusy(false); }
  }, [generation, marketplace, notes, brand, onSpent]);

  const mk = (meta?.marketplaces || []).find((m) => m.key === marketplace);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/85 p-4 sm:p-8"
         onClick={onClose} data-testid="listing-modal">
      <div className="w-full max-w-3xl rounded-2xl border border-[#2A2A30] bg-[#0E0E10]"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#2A2A30] px-5 py-3.5">
          <h2 className="flex items-center gap-2 font-serif-display text-lg">
            <Tag size={15} className="text-white/40" /> Write the listing
          </h2>
          <button onClick={onClose} data-testid="listing-close" className="text-white/40 hover:text-white">
            <X size={17} />
          </button>
        </div>

        <div className="grid gap-5 p-5 sm:grid-cols-[180px_1fr]">
          <div>
            <img src={fileUrl(generation.url)} alt=""
                 className="w-full rounded-xl border border-[#2A2A30] object-cover" />
            <p className="mt-2 text-[11px] leading-snug text-white/35">
              The copy is read off this photograph. Nothing outside the frame is known
              to it.
            </p>
          </div>

          <div className="space-y-4">
            {meta && !meta.ready ? (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                <p className="text-sm text-amber-200/90">
                  None of your keys can read a photograph yet. This needs one whose text
                  model has eyes — {(meta.needs || []).join(', ')}. The others write text
                  fine but cannot look at the picture.
                </p>
                <Link to="/app/keys" className="mt-2 inline-block text-xs text-amber-300 underline">
                  Open API Keys
                </Link>
              </div>
            ) : null}

            {!out && (
              <>
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-white/40">Where you are listing</label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {(meta?.marketplaces || []).map((m) => (
                      <button key={m.key} onClick={() => setMarketplace(m.key)}
                              data-testid={`listing-mk-${m.key}`}
                              className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                                marketplace === m.key
                                  ? 'bg-white text-black'
                                  : 'border border-[#2A2A30] text-white/60 hover:text-white'}`}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                  {mk && (
                    <p className="mt-1.5 text-[11px] text-white/30">
                      {mk.label} allows {mk.title_max} characters of title and {mk.tags} tags
                      of {mk.tag_max}. Anything longer is trimmed here rather than rejected there.
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-wide text-white/40">
                    What the photograph cannot show
                  </label>
                  <textarea
                    value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                    data-testid="listing-notes"
                    placeholder="Size, weight, materials, what is in the box, how it is made. Anything here is trusted over the picture."
                    className="mt-1.5 w-full rounded-xl border border-[#2A2A30] bg-[#141417] p-3 text-sm text-white placeholder:text-white/25 focus:border-white/40 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-wide text-white/40">Shop or brand name</label>
                  <input
                    value={brand} onChange={(e) => setBrand(e.target.value)}
                    data-testid="listing-brand" placeholder="Optional"
                    className="mt-1.5 h-9 w-full rounded-full border border-[#2A2A30] bg-[#141417] px-3.5 text-sm text-white placeholder:text-white/25 focus:border-white/40 focus:outline-none"
                  />
                </div>

                <button onClick={write} disabled={busy || (meta && !meta.ready)}
                        data-testid="listing-write"
                        className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-medium text-black disabled:opacity-40">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Tag size={14} />}
                  {busy ? 'Reading the photograph…' : `Write it — ${meta?.cost ?? '…'} credits`}
                </button>
                {/* Quien lo va a escribir, dicho antes y no despues: el
                    resultado cambia bastante segun el modelo que lo atienda. */}
                {meta?.providers?.length > 0 && (
                  <p className="mt-2 text-[11px] text-white/30">
                    {meta.providers[0]} will write it
                    {meta.providers.length > 1 && `, or ${meta.providers.slice(1).join(' or ')} if it is unavailable`}.
                  </p>
                )}
              </>
            )}

            {out && (
              <div className="space-y-3">
                {/* Lo primero que se lee, y a proposito. El unico fallo que le
                    cuesta dinero a un vendedor es el detalle inventado, y esto
                    lo hace comprobable antes de publicar en vez de despues. */}
                <div className="rounded-xl border border-[#2A2A30] bg-[#141417] p-3">
                  <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-white/40">
                    <Eye size={11} /> What it saw
                  </span>
                  <p className="mt-1.5 text-sm italic text-white/60">{out.observed}</p>
                  <p className="mt-1.5 text-[11px] text-white/30">
                    If this describes the wrong thing, the rest is wrong too. Fix the notes
                    and write it again.
                  </p>
                </div>

                {(out.warnings || []).length > 0 && (
                  <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-3">
                    {out.warnings.map((w) => (
                      <p key={w} className="flex items-start gap-1.5 text-[12px] text-amber-200/80">
                        <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {w}
                      </p>
                    ))}
                  </div>
                )}

                <Field label="Title" testid="title" value={out.title}
                       hint={`${(out.title || '').length}/${mk?.title_max ?? ''}`} />

                {out.bullets?.length > 0 && (
                  <Field label="Bullets" testid="bullets" multiline
                         hint={`${out.bullets.length}`}
                         value={out.bullets.map((b) => `• ${b}`).join('\n')} />
                )}

                <Field label="Description" testid="description" multiline value={out.description} />

                {out.tags?.length > 0 && (
                  <Field label="Tags" testid="tags" hint={`${out.tags.length}/${mk?.tags ?? ''}`}
                         value={out.tags.join(', ')} />
                )}

                <Field label="Alt text" testid="alt" value={out.alt_text}
                       hint={`${(out.alt_text || '').length}/125`} />

                {out.materials?.length > 0 && (
                  <Field label="Materials it can see" testid="materials" value={out.materials.join(', ')} />
                )}

                {out.uncertain?.length > 0 && (
                  <div className="rounded-xl border border-dashed border-[#2A2A30] p-3">
                    <span className="text-[11px] uppercase tracking-wide text-white/40">
                      You still have to say
                    </span>
                    <ul className="mt-1.5 space-y-1">
                      {out.uncertain.map((u) => (
                        <li key={u} className="text-[13px] text-white/55">— {u}</li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[11px] text-white/30">
                      Left out on purpose: a photograph cannot settle these, and a guessed
                      measurement comes back as a return.
                    </p>
                  </div>
                )}

                <button onClick={() => setOut(null)} data-testid="listing-again"
                        className="rounded-full border border-[#2A2A30] px-4 py-1.5 text-xs text-white/70 hover:border-white/40 hover:text-white">
                  Write another, with different notes
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
